import { getDefaultStore } from 'jotai';
import {
  mcpBridgeLogAtom,
  type MCPBridgeLogEntry,
  type MCPBridgeLogLevel,
  type MCPBridgeSpeaker,
} from './mcpBridgeLogAtom';
import { toSafeString, truncate } from './logString';

// MCPBridgeConnection / MCPBridgeMessageHandler が DI で受け取る際の型。
// クラス型 MCPBridgeLogger をそのまま引数型にすると、テストの mock オブジェクトが
// private メンバー（append など）を持たないために構造的部分型で弾かれるため、公開 API のみを切り出す。
export interface MCPBridgeLoggerLike {
  success(message: string): void;
  info(message: string): void;
  warn(message: string): void;
  error(message: string, cause?: unknown): void;
  userSpeech(message: string): void;
  assistantSpeech(message: string): void;
}

export class MCPBridgeLogger implements MCPBridgeLoggerLike {
  // 保持するログの上限件数。1 エントリ最大 1024 字として概算 500KB 未満で、
  // 長期接続でもメモリ問題が生じない上限。
  private static readonly MAX_ENTRIES = 500;

  success(message: string): void {
    this.append('success', message);
  }

  info(message: string): void {
    this.append('info', message);
  }

  warn(message: string): void {
    this.append('warn', message);
  }

  // error は例外の握り潰し経路で使われる想定のため、第二引数に unknown の cause を受け取る。
  // cause から意味のある文字列が取り出せたときだけ ": <text>" を末尾に連結する。
  // extractCauseText 側で「連結すべきものが無い（undefined / null / message が空文字）」を
  // 空文字に倒すことで、`"failed: "` のような dangling colon が MCPBridgeLogView に残る事故を防ぐ。
  // 長さ制約は pushEntry の truncate で吸収する。
  error(message: string, cause?: unknown): void {
    const causeText = extractCauseText(cause);
    this.append('error', causeText === '' ? message : `${message}: ${causeText}`);
  }

  userSpeech(message: string): void {
    this.appendSpeech('user', message);
  }

  assistantSpeech(message: string): void {
    this.appendSpeech('assistant', message);
  }

  private append(level: MCPBridgeLogLevel, message: string): void {
    const timestamp = Date.now();
    this.pushEntry(message, (id, truncated) => ({ kind: 'log', id, timestamp, level, message: truncated }));
  }

  private appendSpeech(speaker: MCPBridgeSpeaker, message: string): void {
    const timestamp = Date.now();
    this.pushEntry(message, (id, truncated) => ({
      kind: 'speech',
      id,
      timestamp,
      speaker,
      message: truncated,
    }));
  }

  // atom に積むメッセージの truncate は、この 1 箇所で確定させる。
  // 各 append 系メソッドで truncate を書き回さずに「1 エントリ最大 1024 字」の
  // 不変条件を構造的に守る。
  private pushEntry(rawMessage: string, build: (id: number, truncatedMessage: string) => MCPBridgeLogEntry): void {
    const truncated = truncate(rawMessage);
    getDefaultStore().set(mcpBridgeLogAtom, (prev) => {
      // id はインスタンスではなく atom 側の直近エントリから採番する。
      // MCPBridgeLogger は再接続のたびに複数回インスタンス化されうるため、
      // インスタンス単位のカウンタでは atom 内で id が重複しうる。
      const id = (prev.at(-1)?.id ?? -1) + 1;
      return [...prev, build(id, truncated)].slice(-MCPBridgeLogger.MAX_ENTRIES);
    });
  }
}

// cause から「連結する価値のある文字列」を取り出す。
// - undefined / null: そもそも cause が無い扱い。空文字を返して呼び出し側で連結を抑止させる
// - Error インスタンス: .message を返す（空文字ならそのまま空文字。連結抑止に倒す）
// - `.message` が string の POJO: AI SDK の fetch 経路や tRPC middleware は Error ではない
//   `{message, status, ...}` を投げることがあるため、duck-typing で .message を拾う
//   （instanceof Error は cross-realm / POJO で false になり message が失われる）
// - それ以外: toSafeString で安全に文字列化。toString が throw する病的な値でも
//   [unserializable ...] に倒れるため、ログ経路自体が例外で沈黙する事故を防ぐ
function extractCauseText(cause: unknown): string {
  if (cause === undefined || cause === null) {
    return '';
  }
  if (cause instanceof Error) {
    return cause.message;
  }
  // POJO の `.message` 参照は Proxy の has/get trap や getter が throw する可能性があるため
  // try/catch で保護する。失敗時は toSafeString にフォールバックしてログ経路自体の沈黙を防ぐ。
  if (typeof cause === 'object') {
    try {
      if ('message' in cause && typeof (cause as { message: unknown }).message === 'string') {
        return (cause as { message: string }).message;
      }
    } catch {
      return toSafeString(cause);
    }
  }
  return toSafeString(cause);
}
